import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSettings } from "../../contexts/SettingsContext";
import LanguageSelector from "../forms/LanguageSelector";

interface SettingsModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSettingsChanged?: () => void;
}

const SettingsModal: React.FC<SettingsModalProps> = ({
    isOpen,
    onClose,
    onSettingsChanged,
}) => {
    const { t, i18n } = useTranslation();
    const { settings, updateSettings } = useSettings();
    
    const [showAdultContent, setShowAdultContent] = useState(false);
    const [selectedLanguage, setSelectedLanguage] = useState(i18n.language);
    const [overrideBrowserLanguage, setOverrideBrowserLanguage] = useState(false);
    const [availableLanguages, setAvailableLanguages] = useState<string[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const loadUserSettings = async () => {
            if (!isOpen || !settings) return;

            setLoading(false);
            setError(null);

            try {
                const override = settings.override_browser_language || false;
                setOverrideBrowserLanguage(override);
                
                // If override is enabled, use the stored language preference
                // If override is disabled, show the current detected language
                if (override) {
                    setSelectedLanguage(settings.language);
                } else {
                    setSelectedLanguage(i18n.language);
                }
                
                setShowAdultContent(settings.show_adult_content);
                setAvailableLanguages(settings.available_languages || []);
            } catch (error) {
                
                setError(t('common.failedToLoadSettings'));

                // Fallback to localStorage if context fails
                const savedAdultContentSetting = localStorage.getItem("showAdultContent");
                setShowAdultContent(savedAdultContentSetting === "true");
            }
        };

        loadUserSettings();
    }, [isOpen, settings, t, i18n.language]);

    useEffect(() => {
        const handleEscapeKey = (event: KeyboardEvent) => {
            if (event.key === "Escape" && isOpen) {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener("keydown", handleEscapeKey);
        }

        return () => {
            document.removeEventListener("keydown", handleEscapeKey);
        };
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    const handleSave = async () => {
        setLoading(true);
        setError(null);

        try {
            const settingsUpdate = {
                language: selectedLanguage,
                show_adult_content: showAdultContent,
                override_browser_language: overrideBrowserLanguage,
                available_languages: availableLanguages,
            };

            await updateSettings(settingsUpdate);

            // Language will be automatically updated by the SettingsContext

            if (onSettingsChanged) {
                onSettingsChanged();
            }
            onClose();
        } catch (error) {
            
            setError(t('common.failedToSaveSettings'));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-gray-600 dark:bg-gray-900 bg-opacity-50 dark:bg-opacity-75 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border border-gray-200 dark:border-gray-700 w-[480px] shadow-lg rounded-md bg-white dark:bg-gray-800">
                <div className="mt-3">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
                        {t("settings.settings")}
                    </h3>

                    {error && (
                        <div className="mb-4 p-3 bg-yellow-100 dark:bg-yellow-900 border border-yellow-400 dark:border-yellow-600 text-yellow-700 dark:text-yellow-200 rounded">
                            {error}
                        </div>
                    )}

                    {loading ? (
                        <div className="flex justify-center items-center py-8">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            <div>
                                <div className="flex items-center justify-between">
                                    <div>
                                        <label
                                            htmlFor="override-language-toggle"
                                            className="text-sm font-medium text-gray-700 dark:text-gray-300"
                                        >
                                            {t("settingsModal.overrideBrowserLanguage")}
                                        </label>
                                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                            {t("settingsModal.overrideBrowserLanguageDescription")}
                                        </p>
                                    </div>
                                    <button
                                        id="override-language-toggle"
                                        type="button"
                                        className={`${
                                            overrideBrowserLanguage
                                                ? "bg-blue-600"
                                                : "bg-gray-200 dark:bg-gray-600"
                                        } relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2`}
                                        onClick={() => setOverrideBrowserLanguage(!overrideBrowserLanguage)}
                                    >
                                        <span
                                            className={`${
                                                overrideBrowserLanguage
                                                    ? "translate-x-5"
                                                    : "translate-x-0"
                                            } pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
                                        />
                                    </button>
                                </div>
                                
                                <div className="flex items-center justify-between mt-4">
                                    <label
                                        htmlFor="language-select"
                                        className="text-sm font-medium text-gray-700 dark:text-gray-300"
                                    >
                                        {t("common.language")}
                                    </label>
                                    <select
                                        id="language-select"
                                        value={selectedLanguage}
                                        onChange={(e) =>
                                            setSelectedLanguage(e.target.value)
                                        }
                                        disabled={!overrideBrowserLanguage}
                                        className={`px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-blue-500 focus:border-blue-500 ${
                                            !overrideBrowserLanguage ? 'opacity-50 cursor-not-allowed' : ''
                                        }`}
                                    >
                                        <option value="en">
                                            {t("settingsModal.english")}
                                        </option>
                                        <option value="da">
                                            {t("settingsModal.danish")}
                                        </option>
                                    </select>
                                </div>
                            </div>

                            <div>
                                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
                                    {t("settingsModal.availableComicLanguages")}
                                </label>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                                    {t(
                                        "settingsModal.availableLanguagesDescription"
                                    )}
                                </p>
                                <LanguageSelector
                                    selectedLanguages={availableLanguages}
                                    onLanguagesChange={setAvailableLanguages}
                                    placeholder={t(
                                        "settingsModal.selectLanguagesPlaceholder"
                                    )}
                                />
                            </div>

                            <div className="flex items-center justify-between">
                                <label
                                    htmlFor="adult-content-toggle"
                                    className="text-sm font-medium text-gray-700 dark:text-gray-300"
                                >
                                    {t("settings.showAdultContent")}
                                </label>
                                <button
                                    id="adult-content-toggle"
                                    type="button"
                                    className={`${
                                        showAdultContent
                                            ? "bg-blue-600"
                                            : "bg-gray-200 dark:bg-gray-600"
                                    } relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2`}
                                    onClick={() => setShowAdultContent(!showAdultContent)}
                                >
                                    <span
                                        className={`${
                                            showAdultContent
                                                ? "translate-x-5"
                                                : "translate-x-0"
                                        } pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
                                    />
                                </button>
                            </div>
                        </div>
                    )}

                    <div className="flex justify-end space-x-3 mt-6">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-md"
                        >
                            {t("common.cancel")}
                        </button>
                        <button
                            onClick={handleSave}
                            disabled={loading}
                            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-md"
                        >
                            {loading ? t("common.saving") : t("common.save")}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SettingsModal;